import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, Users, ShoppingBag, Lock, FolderInput } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TOPICS, TOPIC_GROUPS, topicSection } from '@/lib/topics';
import { topicBadge } from '@/components/group/useGroupMenu';

const API = process.env.REACT_APP_BACKEND_URL;

const TopicCard = ({ t, s, onOpen }) => {
  const Icon = t.icon;
  const badge = topicBadge(s);
  return (
    <button type="button" onClick={onOpen} data-testid={`topic-card-${t.key}`}
      className="group text-left flex items-center gap-3 p-3.5 rounded-xl border bg-card hover:border-green-500/50 hover:shadow-sm transition-[border-color,box-shadow]">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"><Icon className="h-5 w-5" /></div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{t.label}</p>
        <p className="text-xs text-muted-foreground truncate">{t.hint}</p>
      </div>
      {badge && <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${s.overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`} data-testid={`topic-badge-${t.key}`}>{badge}</span>}
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
    </button>
  );
};

function UnfiledItems({ projectId }) {
  const [items, setItems] = useState(null);
  const [pick, setPick] = useState({});
  const load = () => axios.get(`${API}/api/topics/${projectId}/admin/unfiled`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items?.length) return null;
  const file = (it) => axios.put(`${API}/api/topics/${projectId}/admin/file`, { kind: it.kind, id: it.id, topics: pick[it.id] || [] })
    .then(() => { toast.success('Filed'); load(); }).catch((e) => toast.error(e.response?.data?.detail || 'Could not file'));
  const toggle = (id, k) => setPick((p) => ({ ...p, [id]: (p[id] || []).includes(k) ? p[id].filter((x) => x !== k) : [...(p[id] || []), k] }));
  return (
    <section className="rounded-xl border border-dashed p-4 space-y-3" data-testid="unfiled-items">
      <p className="font-semibold text-sm flex items-center gap-2"><FolderInput className="h-4 w-4" />Not yet filed under a topic ({items.length})</p>
      <p className="text-xs text-muted-foreground">Admins only. Pick every topic the item touches; it then shows in each of those rooms.</p>
      {items.map((it) => (
        <div key={it.id} className="border rounded-lg p-3 space-y-2" data-testid={`unfiled-${it.id}`}>
          <p className="text-sm"><span className="text-[10px] uppercase text-muted-foreground mr-2">{it.kind}</span>{it.title}</p>
          <div className="flex flex-wrap gap-1">
            {TOPICS.map((t) => (
              <button key={t.key} type="button" onClick={() => toggle(it.id, t.key)}
                className={`text-[11px] px-2 py-0.5 rounded-full border ${(pick[it.id] || []).includes(t.key) ? 'bg-foreground text-background' : ''}`}>{t.label}</button>
            ))}
          </div>
          <Button size="sm" className="h-7" disabled={!(pick[it.id] || []).length} onClick={() => file(it)} data-testid={`unfiled-save-${it.id}`}>File</Button>
        </div>
      ))}
    </section>
  );
}

// Buyers' Group main page: one card per topic, plus the two common tools.
export default function TopicsLanding({ projectId, onNavigate, isVerified, isAdmin }) {
  const [summary, setSummary] = useState({});
  useEffect(() => {
    if (!projectId) return;
    axios.get(`${API}/api/topics/${projectId}`).then((r) => setSummary(Object.fromEntries(r.data.topics.map((t) => [t.key, t])))).catch(() => {});
  }, [projectId]);
  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="topics-landing">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Buyers' Group</h1>
        <p className="text-sm text-muted-foreground">Every issue between buyers and the developer, one room per topic: status, letters, documents, meetings and discussion.</p>
      </div>
      {!isVerified && (
        <div className="rounded-lg border bg-muted/40 p-3 text-sm flex items-center gap-2" data-testid="topics-locked-note">
          <Lock className="h-4 w-4 shrink-0" />Rooms are for verified flat owners. You can see which topics are active; verify ownership to open them.
          <Button size="sm" variant="outline" className="ml-auto h-7 shrink-0" onClick={() => onNavigate('verify-ownership')}>Verify</Button>
        </div>
      )}
      {TOPIC_GROUPS.map((g) => (
        <section key={g.key} id={`topic-group-${g.key}`} className="space-y-2 scroll-mt-20" data-testid={`topic-group-${g.key}`}>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</h2>
            <p className="text-xs text-muted-foreground">{g.hint}</p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {TOPICS.filter((t) => t.group === g.key).map((t) => (
              <TopicCard key={t.key} t={t} s={summary[t.key]} onOpen={() => onNavigate(topicSection(t.key))} />
            ))}
          </div>
        </section>
      ))}
      <section className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Common tools</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <TopicCard t={{ key: 'members', label: 'Members', hint: 'Who is in your group', icon: Users }} onOpen={() => onNavigate('us-directory')} />
          <TopicCard t={{ key: 'classifieds', label: 'Classifieds', hint: 'Buy, rent or lease within the project', icon: ShoppingBag }} onOpen={() => onNavigate('property-marketplace')} />
        </div>
      </section>
      {isAdmin && <UnfiledItems projectId={projectId} />}
    </div>
  );
}
